import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Hammer, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO = "https://images.unsplash.com/photo-1597960194599-22929afc25b1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Bem-vindo de volta!");
      } else {
        await register(form);
        toast.success("Conta criada com sucesso!");
      }
      navigate(location.state?.from || "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <Hammer className="h-10 w-10 mb-4" />
          <h2 className="text-3xl font-extrabold tracking-tight">Transforme madeira em ideias.</h2>
          <p className="mt-2 text-white/85 max-w-md">A comunidade brasileira de projetos DIY com paletes.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Logo className="mb-8" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {isLogin ? "Entrar na sua conta" : "Criar sua conta"}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {isLogin ? "Acesse para curtir, salvar e publicar projetos." : "Junte-se a makers de todo o Brasil."}
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            {!isLogin && (
              <>
                <Field label="Nome" testid="auth-name" value={form.name} onChange={update("name")} placeholder="Seu nome completo" required />
                <Field label="Nome de usuário" testid="auth-username" value={form.username} onChange={update("username")} placeholder="seunome" required />
              </>
            )}
            <Field label="E-mail" testid="auth-email" type="email" value={form.email} onChange={update("email")} placeholder="voce@email.com" required />
            <Field label="Senha" testid="auth-password" type="password" value={form.password} onChange={update("password")} placeholder="Mínimo 6 caracteres" required />

            {error && (
              <p data-testid="auth-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
            )}

            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full rounded-full font-semibold" size="lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isLogin ? "Entrar" : "Criar conta"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />ou<span className="h-px flex-1 bg-border" /></div>
          <Button asChild variant="outline" size="lg" className="w-full rounded-full font-semibold gap-2" data-testid="google-login-btn">
            <Link to="/sign-in"><GoogleIcon /> Continuar com Google</Link>
          </Button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? (
              <>Não tem conta? <Link to="/cadastro" className="font-semibold text-primary hover:underline">Cadastre-se</Link></>
            ) : (
              <>Já tem conta? <Link to="/entrar" className="font-semibold text-primary hover:underline">Entrar</Link></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <Input data-testid={testid} className="mt-1.5 h-11 rounded-xl" {...props} />
    </div>
  );
}

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);
